/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from '@vlinder-web/plugin-agent-service-react';
import {
  AppServiceMachineType,
  AppServiceRequestProcessor,
} from '@services/state-machine';
import config from '../config/analyticsWidget.config.json';
import { VApplication } from '@/application';
import { GraphqlServerModule } from '@vlinder-web/graphql-server-module-react';
import { GET_DASHBOARD_ANALYTICS_QUERY } from '../graphql/getDashBoardAnalytics.query';
import { AnalyticsResultFormatterMessage } from '../messages/analytics-result-formatter.message';
import { ANALYTICS_RESULT_FORMATTER_CONFIG } from '../machines/analytics-result-formatter/config';
import { StorageModule } from '@vlinder-web/storage-module-react';

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync('services.GraphqlServerModule') as GraphqlServerModule;
};

export interface IAnalyticsWidgetMethods extends IPluginMethodMap {
  getAnalyticsWidgetProps(args: any): Promise<any>;
}

export class AnalyticeWidgetService
  extends AppServiceRequestProcessor
  implements IAgentPlugin
{
  readonly methods: IAnalyticsWidgetMethods;

  constructor() {
    super();
    this.methods = {
      getAnalyticsWidgetProps: this.getProps.bind(this),
    };
  }

  async getProps(args: any) {
    const storageService = StorageModule?.getInstance();
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient('graphql-gateway');
    const orgDetails = storageService?.get('myProfile', 'local')?.orgData;
    const orgId = orgDetails?.id;
    try {
      if (client) {
        const res = await client.query({
          query: GET_DASHBOARD_ANALYTICS_QUERY,
          variables: { where: { id: orgId } },
          fetchPolicy: 'network-only',
        });
        if (res?.data?.orgs) {
          const message = new AnalyticsResultFormatterMessage({
            raw: this._getRaw(res?.data?.orgs),
            template: { ...(config || {}) },
          });
          console.log(`message`, message);
          this.startSyncProcessing({
            machineConfig: {
              ...ANALYTICS_RESULT_FORMATTER_CONFIG,
              data: {
                message: message,
              },
            },
            machineType: AppServiceMachineType.DASHBOARD_ANALYTICS_FORMATTER,
          });
          return message?.template;
        }
      }
    } catch (err: any) {
      console.log(`error in getDashboardQuery`, err);
      throw Error(err?.message);
    }
  }

  private _getRaw(data: any[]) {
    let res: any[] = [];
    data &&
      Array.isArray(data) &&
      data.length > 0 &&
      data.forEach((item) => {
        res.push({
          totalSupplyChainTemplates: {
            raw: 1 ?? item?.supplyChainTemplatesAggregate?.count,
          },
          totalCompliance: { raw: item?.complianceDocsAggregate?.count },
          totalSuppliers: { raw: item?.childrenOrgsAggregate?.count },
        });
      });

    return res[0];
  }
}
